import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import type { MemberInfo } from "../features/auth/useMember";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
}

const HomeIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5 12 4l9 6.5" />
    <path d="M5.5 9.5V20h13V9.5" />
    <path d="M10 20v-5h4v5" />
  </svg>
);
const ChatIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12.5c0 3.6-3.6 6.5-8 6.5-.9 0-1.8-.1-2.6-.35L5 21l1.1-3.1C4.8 16.6 4 14.7 4 12.5 4 8.9 7.6 6 12 6s8 2.9 8 6.5Z" />
  </svg>
);
const LibraryIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3.5 21 8l-9 4.5L3 8l9-4.5Z" />
    <path d="M4.5 12.5 12 16.2l7.5-3.7" />
    <path d="M4.5 16.8 12 20.5l7.5-3.7" />
  </svg>
);
const PodcastIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="11" r="3.2" />
    <path d="M8.5 15.5 7 21M15.5 15.5 17 21M6 8a6 6 0 0 1 12 0" />
  </svg>
);
const AccountIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8.5" r="3.2" />
    <path d="M5 20c1.2-3.6 4-5.5 7-5.5s5.8 1.9 7 5.5" />
  </svg>
);
const ImportIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 16V4" />
    <path d="M7.5 8.5 12 4l4.5 4.5" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </svg>
);
const AdminIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 3v2.4M12 18.6V21M3 12h2.4M18.6 12H21M5.6 5.6l1.7 1.7M16.7 16.7l1.7 1.7M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7" />
  </svg>
);

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Start", icon: <HomeIcon /> },
  { to: "/chat", label: "Fragen", icon: <ChatIcon /> },
  { to: "/library", label: "Bibliothek", icon: <LibraryIcon /> },
  { to: "/podcast", label: "Podcast", icon: <PodcastIcon /> },
  { to: "/account", label: "Konto", icon: <AccountIcon /> },
  { to: "/import", label: "Import", icon: <ImportIcon />, adminOnly: true },
  { to: "/admin", label: "Admin", icon: <AdminIcon />, adminOnly: true }
];

export function AppShell({ member, children }: { member: MemberInfo; children: ReactNode }) {
  const items = NAV_ITEMS.filter((item) => !item.adminOnly || member.role === "admin");

  return (
    <div className="pw-shell">
      <a href="#pw-main" className="pw-skip-link">
        Zum Inhalt springen
      </a>
      <nav className="pw-nav-rail" aria-label="Hauptnavigation">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) => `pw-nav-rail__item${isActive ? " pw-nav-rail__item--active" : ""}`}
            title={item.label}
          >
            {item.icon}
            <span className="pw-visually-hidden">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="pw-shell__main-col">
        <header className="pw-header">
          <span className="pw-header__brand">
            <span className="pw-header__title">Parawissen</span>
            <span className="pw-header__subtitle">{member.displayName}</span>
          </span>
        </header>

        <main id="pw-main" className="pw-main">
          {children}
        </main>

        <nav className="pw-nav-bottom" aria-label="Hauptnavigation">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `pw-nav-bottom__item${isActive ? " pw-nav-bottom__item--active" : ""}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
